import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Drawer } from '@mui/material';
import useInfiniteScroll from '../../../hook/useInfiniteScroll';
import { useEventListQuery } from '../../../queries';
import PostCard from './PostCard';
import PostDetail from './PostDetail';

const Sort = {
  Registration: 1,
  Latest: 2,
};

const LIMIT = 10;

function Event() {
  const pushedRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [list, setList] = useState([]);
  const [contentOpenId, setContentOpenId] = useState(null);
  const [sorting, setSorting] = useState(Sort.Latest);

  const apiParams = useMemo(
    () => ({
      limit: LIMIT,
      page,
      order: sorting === Sort.Registration ? 'ASC' : 'DESC',
      sortBy: sorting === Sort.Registration ? 'createdAt' : 'updatedAt',
    }),
    [page, sorting]
  );

  const { data, isLoading, refetch } = useEventListQuery(apiParams);

  useEffect(() => {
    if (data) {
      const sortedEvents = [...data.events].sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
      });
      if (page === 1) {
        setList(sortedEvents);
      } else {
        setList((prev) => [...prev, ...sortedEvents]);
      }
      setTotalCount(data.total);
    }
  }, [data, page]);

  const refreshLists = useCallback(() => {
    setPage(1);
    setList([]);
    setHasMore(true);
    refetch();
  }, [refetch]);

  const prevSortingRef = useRef(Sort.Latest);
  // 등록순/최신순 변경 시 refresh
  useEffect(() => {
    if (prevSortingRef.current === sorting) return;
    refreshLists();
    prevSortingRef.current = sorting;
  }, [sorting, refreshLists]);

  // infinite scroll
  const { lastItemRef } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore: () => {
      if (totalCount <= page * LIMIT) {
        setHasMore(false);
        return;
      }
      setPage((prev) => prev + 1);
    },
  });

  useEffect(() => {
    if (sorting === Sort.Latest) {
      setList((prevNotices: any[]) =>
        [...prevNotices].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      );
    } else {
      setList((prevNotices: any[]) =>
        [...prevNotices].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )
      );
    }
  }, [sorting]);

  // 뒤로가기에 드로워가 닫히도록 설정
  const openDrawer = useCallback((id) => {
    setContentOpenId(id);
    window.history.pushState({ drawer: true }, '');
    pushedRef.current = true;
  }, []);

  const closeDrawer = useCallback(() => {
    setContentOpenId(null);
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (contentOpenId !== null) {
        setContentOpenId(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [contentOpenId]);

  return (
    <div className="relative w-full">
      <div className="w-full flex justify-end items-center my-5 text-[12px] font-[700] gap-2">
        <button
          type="button"
          style={{ color: sorting === Sort.Registration ? '#363636' : '#969696' }}
          onClick={() => setSorting(Sort.Registration)}
        >
          등록순
        </button>
        <button
          type="button"
          style={{ color: sorting === Sort.Latest ? '#363636' : '#969696' }}
          onClick={() => setSorting(Sort.Latest)}
        >
          최신순
        </button>
      </div>
      <div className="w-full">
        {list?.length >= 1 &&
          list?.map((content, idx) => {
            return (
              <PostCard
                key={content.id}
                post={content}
                onClick={openDrawer}
                ref={idx === list.length - 1 ? lastItemRef : undefined}
              />
            );
          })}
      </div>
      <Drawer
        open={contentOpenId !== null}
        onClose={closeDrawer}
        anchor="right"
        sx={{
          '& .MuiDrawer-paper': {
            width: '100vw',
            maxWidth: '100vw',
          },
        }}
      >
        <PostDetail
          drawerOpen={contentOpenId !== null}
          onClickPrev={closeDrawer}
          postId={contentOpenId}
        />
      </Drawer>
    </div>
  );
}

export default Event;
